import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UserData from '../data/UserData';

const ProfileEditModal = React.memo(({ open, onClose, initialData, onUpdate, isCreateMode = false }) => {
  //console.log('Modal initialData:', initialData);
  
  const [formData, setFormData] = React.useState({
    email: '',
    nombre: '',
    apellidoPaterno: '',
    apellidoMaterno: '',
    calle: '',
    numeroExterior: '',
    numeroInterior: '',
    codigoPostal: '',
    colonia: '',
    telefono: '',
    celular: '',
    otroEmail: ''
  });
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [validationError, setValidationError] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [showError, setShowError] = React.useState(false);

  // Only update form data when modal opens with new initialData
  React.useEffect(() => {
    if (open && initialData) {
      setFormData({
        email: initialData.email || '',
        nombre: initialData.nombre || '',
        apellidoPaterno: initialData.apellidoPaterno || '',
        apellidoMaterno: initialData.apellidoMaterno || '',
        calle: initialData.calle || '',
        numeroExterior: initialData.numeroExterior || '',
        numeroInterior: initialData.numeroInterior || '',
        codigoPostal: initialData.codigoPostal || '',
        colonia: initialData.colonia || '',
        telefono: initialData.telefono || '',
        celular: initialData.celular || '',
        otroEmail: initialData.otroEmail || ''
      });
    }
  }, [open, initialData]);

  const handleChange = React.useCallback((e) => {
    const { name, value } = e.target;
    // Prevent alphanumeric input for numeroInterior (digits only)
    const nextValue = name === 'numeroInterior' ? value.replace(/\D/g, '') : value;
    setFormData(prev => ({
      ...prev,
      [name]: nextValue
    }));
  }, []);

  const validateForm = React.useCallback(() => {
    setValidationError('');
    
    // Required fields validation
    const requiredFields = {
      nombre: 'Nombre',
      apellidoPaterno: 'Apellido Paterno',
      apellidoMaterno: 'Apellido Materno',
      calle: 'Calle',
      numeroExterior: 'Número Exterior',
      codigoPostal: 'Código Postal',
      colonia: 'Colonia'
    };

    for (const [field, label] of Object.entries(requiredFields)) {
      const value = formData[field];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        setValidationError(`El campo "${label}" es obligatorio`);
        return false;
      }
    }

    // At least one phone number is required
    if (!formData.telefono && !formData.celular) {
      setValidationError('Debe proporcionar al menos un número de teléfono (teléfono o celular)');
      return false;
    }

    return true;
  }, [formData]);

  const handleSubmit = React.useCallback(async (e) => {
    e.preventDefault();
    
    // Validate form before submitting
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      if (isCreateMode) {
        await UserData.create(formData);
      } else {
        await UserData.update(formData);
      }
      if (formData.email) {
        try {
          localStorage.setItem(`profileUpdateYear_${formData.email}`, String(new Date().getFullYear()));
        } catch (_) {}
      }
      if (onUpdate) {
        onUpdate();
      }
      setShowSuccess(true);
      setTimeout(() => {
        onClose();
        setShowSuccess(false);
      }, 1500);
    } catch (error) {
      console.error(`Error ${isCreateMode ? 'creating' : 'updating'} profile:`, error);
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 3000);
    } finally {
      setIsLoading(false);
    }
  }, [formData, onUpdate, onClose, isCreateMode, validateForm]);

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: '1.5rem',
          fontWeight: 'bold'
        }}>
          {isCreateMode ? 'Registro de Padre' : 'Editar Perfil'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {validationError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {validationError}
              </Alert>
            )}
            {showError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Error al crear, inténtelo nuevamente
              </Alert>
            )}
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    value={formData.email}
                    disabled
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Otro Email de Contacto"
                    name="otroEmail"
                    value={formData.otroEmail}
                    onChange={handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Apellido Paterno"
                    name="apellidoPaterno"
                    value={formData.apellidoPaterno}
                    onChange={handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Apellido Materno"
                    name="apellidoMaterno"
                    value={formData.apellidoMaterno}
                    onChange={handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Calle"
                    name="calle"
                    value={formData.calle}
                    onChange={handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    required
                    label="N. Exterior"
                    name="numeroExterior"
                    value={formData.numeroExterior}
                    onChange={handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="N. Interior"
                    name="numeroInterior"
                    value={formData.numeroInterior}
                    onChange={handleChange}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    helperText="Solo números"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Colonia"
                    name="colonia"
                    value={formData.colonia}
                    onChange={handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Código Postal"
                    name="codigoPostal"
                    value={formData.codigoPostal}
                    onChange={handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Teléfono (al menos uno requerido)"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Celular (al menos uno requerido)"
                    name="celular"
                    value={formData.celular}
                    onChange={handleChange}
                    margin="normal"
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={isLoading}>Cancelar</Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isLoading ? 'Procesando...' : (isCreateMode ? 'Crear Registro' : 'Guardar Cambios')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={showSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiAlert-root': {
            backgroundColor: '#4caf50',
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white'
            }
          }
        }}
      >
        <Alert
          icon={<CheckCircleIcon />}
          severity="success"
          sx={{ width: '100%' }}
        >
          {isCreateMode ? '¡Éxito, registro creado!' : 'Datos Actualizados'}
        </Alert>
      </Snackbar>
    </>
  );
});

ProfileEditModal.displayName = 'ProfileEditModal';

export default ProfileEditModal; 